import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";
import { makeStyles } from "@material-ui/core";
import { shallowEqual, useSelector } from "react-redux";
import { createMetodoPagamentoMapping, roles } from "../../config/config";
import { ShopGridProvider } from "./context/ShopGridContext";
import { useShopCart } from "./hooks/useShopCart";
import { useDebouncedSconto } from "./hooks/useDebouncedSconto";
import { useDiscountsValues } from "./hooks/useDiscountsValues";
import { useDiscountOptions } from "./hooks/useDiscountOptions";
import { useClientiOptions } from "./hooks/useClientiOptions";
import {
  buildMissingDiscountMessage,
  getDiscountFinalValue,
  getInvalidDiscountProducts,
  isValidDiscount,
  normalizeProductKey,
} from "./utils/shopGridDiscounts";
import { ShopGridFilters } from "./components/ShopGridFilters";
import { ShopProductCard } from "./components/ShopProductCard";
import { ShopCartDropdown } from "./components/ShopCartDropdown";
import { ShopFinalizeDialog } from "./components/ShopFinalizeDialog";

const divisionNames = {
  0: "Tutti",
  1: "RoyPet",
  2: "Roydermal",
  11: "Valderma",
};

const useStyles = makeStyles({
  valderma: {
    borderColor: "#003366",
    color: "#003366",
  },
  roypet: {
    borderColor: "#313d85",
    color: "#313d85",
  },
  roydermal: {
    borderColor: "#c9207c",
    color: "#85241d",
  },
});

const getDivisionClassName = (classes, division) => {
  const div = parseInt(division, 10);
  if (div === 11) return classes.valderma;
  if (div === 2) return classes.roydermal;
  return classes.roypet;
};

export const ShopGrid = ({
  elements,
  cartName,
  displayName,
  richiediExtra,
  richiediDatiVendita,
  api,
  redirectTo,
  maxDescriptionLength = 50,
  imageHeight = 100,
}) => {
  const classes = useStyles();

  const user = useSelector((state) => state.auth.user, shallowEqual);
  const role = parseInt(user.idRole, 10);

  const color = useMemo(
    () => (role === roles.agenteValderma ? "#003366" : role === roles.agenteRoypet ? "#021c5e" : "#da291c"),
    [role]
  );

  const metodoPagamentoMapping = useMemo(() => createMetodoPagamentoMapping(roles, role), [role]);

  const elementsMap = useMemo(
    () => new Map((elements || []).map((e) => [normalizeProductKey(e.id), e])),
    [elements]
  );
  const hasColli = useMemo(() => (elements || []).some((e) => Boolean(e?.quantity)), [elements]);

  const { cart, sconto, getCartValue, setCartValue, setScontoValue, resetCart } = useShopCart({ cartName });

  const { effectiveSconto, getEffectiveDiscountRaw, setDiscountDebounced, flushPendingDiscountUpdates } =
    useDebouncedSconto({ sconto, setScontoValue, debounceMs: 300 });

  const { discountsValues, loading: discountsLoading, error: discountsLoadError } = useDiscountsValues({
    enabled: Boolean(richiediDatiVendita),
  });
  const { getDiscountOptionsForElement } = useDiscountOptions({
    discountsValues,
    discountsLoading,
    discountsLoadError,
  });

  const { clientiOptions } = useClientiOptions({ enabled: Boolean(richiediDatiVendita), authToken: user.authToken });

  const [richiestaLoading, setRichiestaLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState("");
  const [serverErrorMessage, setServerErrorMessage] = useState("");
  const [discountValidationMessage, setDiscountValidationMessage] = useState("");
  const [richiestaExtra, setRichiestaExtra] = useState(false);
  const [richiestaIdCliente, setRichiestaIdCliente] = useState(0);
  const [richiestaMetodoPagamento, setRichiestaMetodoPagamento] = useState(0);
  const [richiestaNote, setRichiestaNote] = useState("");

  const [searchFilter, setSearchFilter] = useState("");
  const [divisionFilter, setDivisionFilter] = useState(0);

  const invalidDiscountProducts = useMemo(
    () =>
      getInvalidDiscountProducts({
        cart,
        sconto: effectiveSconto,
        elementsMap,
        divisionNames,
        richiediDatiVendita,
      }),
    [cart, effectiveSconto, elementsMap, richiediDatiVendita]
  );

  const missingDiscountMessage = useMemo(
    () => buildMissingDiscountMessage(invalidDiscountProducts),
    [invalidDiscountProducts]
  );

  useEffect(() => {
    setDiscountValidationMessage(missingDiscountMessage);
  }, [missingDiscountMessage]);

  const validateDiscountsBeforeSubmit = useCallback(() => {
    if (!richiediDatiVendita) return true;
    if (discountsLoadError) {
      setDiscountValidationMessage("Configurazione sconti non disponibile");
      return false;
    }
    if (invalidDiscountProducts.length > 0) {
      setDiscountValidationMessage(missingDiscountMessage);
      return false;
    }
    flushPendingDiscountUpdates();
    setDiscountValidationMessage("");
    return true;
  }, [
    discountsLoadError,
    flushPendingDiscountUpdates,
    invalidDiscountProducts.length,
    missingDiscountMessage,
    richiediDatiVendita,
  ]);

  const submitRequest = useCallback(
    async ({ idCliente, metodoPagamento, note, extra }) => {
      if (!validateDiscountsBeforeSubmit()) return;

      setRichiestaLoading(true);
      setResult("");
      setServerErrorMessage("");

      const payload = {};
      if (richiediExtra) payload.extra = (extra ? 1 : 0) + 0;
      if (richiediDatiVendita) {
        payload.sconto = effectiveSconto;
        payload.idCliente = Number(idCliente) || 0;
        payload.metodoPagamento = Number(metodoPagamento) || 0;
        payload.metodoPagamentoMapping = metodoPagamentoMapping;
        payload.note = note || "";
      }

      try {
        await api.add(user.authToken, { cart, cartName, ...payload });
        setResult(203);
        resetCart();
        setRichiestaExtra(false);
        setRichiestaIdCliente(0);
        setRichiestaMetodoPagamento(0);
        setRichiestaNote("");
        setDiscountValidationMessage("");
        setServerErrorMessage("");
      } catch (e) {
        setResult(e?.code);
        setServerErrorMessage(e?.plainMessage || "");
      } finally {
        setRichiestaLoading(false);
      }
    },
    [
      api,
      cart,
      cartName,
      effectiveSconto,
      metodoPagamentoMapping,
      resetCart,
      richiediDatiVendita,
      richiediExtra,
      user.authToken,
      validateDiscountsBeforeSubmit,
    ]
  );

  const isDiscountInvalidForProductId = useCallback(
    (productId) => {
      if (!richiediDatiVendita) return false;
      const quantity = getCartValue(productId);
      if (!quantity || quantity <= 0) return false;
      return !isValidDiscount(getEffectiveDiscountRaw(productId));
    },
    [getCartValue, getEffectiveDiscountRaw, richiediDatiVendita]
  );

  const getScontoValue = useCallback(
    (id) => {
      const value = getEffectiveDiscountRaw(id);
      if (typeof value === "undefined" || value === null) return 0;
      return value;
    },
    [getEffectiveDiscountRaw]
  );

  const getScontoFinalValue = useCallback(
    (id) => {
      const parsed = getDiscountFinalValue(getEffectiveDiscountRaw(id));
      return Number.isFinite(parsed) ? parsed : 0;
    },
    [getEffectiveDiscountRaw]
  );

  const onDiscountChange = useCallback(
    (productId, newDiscountValue) => {
      setDiscountDebounced(productId, newDiscountValue);
    },
    [setDiscountDebounced]
  );

  const onSendRequest = useCallback(() => {
    if (richiestaLoading || dialogOpen) return;
    if (Object.keys(cart).length === 0) return;
    if (!validateDiscountsBeforeSubmit()) return;

    setDialogOpen(true);

    if (!richiediDatiVendita && !richiediExtra) {
      submitRequest({ idCliente: 0, metodoPagamento: 0, note: "", extra: false });
    }
  }, [
    cart,
    dialogOpen,
    richiestaLoading,
    richiediDatiVendita,
    richiediExtra,
    submitRequest,
    validateDiscountsBeforeSubmit,
  ]);

  const onDialogClose = useCallback(() => {
    setDiscountValidationMessage("");
    setServerErrorMessage("");
    setDialogOpen(false);
  }, []);

  const onDialogCloseAndReset = useCallback(() => {
    setDialogOpen(false);
    setResult("");
    setDiscountValidationMessage("");
    setServerErrorMessage("");
  }, []);

  const onDialogSubmit = useCallback(
    (values) => {
      const idCliente = parseInt(values.idCliente, 10) || 0;
      const metodoPagamento = parseInt(values.metodoPagamento, 10) || 0;
      const note = values.note || "";

      setRichiestaIdCliente(idCliente);
      setRichiestaMetodoPagamento(metodoPagamento);
      setRichiestaNote(note);

      submitRequest({ idCliente, metodoPagamento, note, extra: richiestaExtra });
    },
    [richiestaExtra, submitRequest]
  );

  const normalizedSearch = useMemo(() => searchFilter.trim().toLowerCase(), [searchFilter]);
  const filteredElements = useMemo(() => {
    const sorted = [...(elements || [])].sort((a, b) => a.division - b.division);
    return sorted.filter((element) => {
      if (![true, 1, "1"].includes(element.available)) return false;
      if (normalizedSearch) {
        const name = (element.name || "").toLowerCase();
        const description = (element.description || "").toLowerCase();
        if (!name.includes(normalizedSearch) && !description.includes(normalizedSearch)) return false;
      }
      if (Number(divisionFilter) > 0 && Number(element.division) !== Number(divisionFilter)) return false;
      return true;
    });
  }, [divisionFilter, elements, normalizedSearch]);

  const ctxValue = useMemo(
    () => ({
      displayName,
      maxDescriptionLength,
      imageHeight,
      getCartValue,
      setCartValue,
      richiediDatiVendita: Boolean(richiediDatiVendita),
      discountsLoading: Boolean(discountsLoading),
      getScontoFinalValue,
      getScontoValue,
      onDiscountChange,
      getDiscountOptionsForElement,
      isDiscountInvalidForProductId,
    }),
    [
      discountsLoading,
      displayName,
      getCartValue,
      getDiscountOptionsForElement,
      getScontoFinalValue,
      getScontoValue,
      imageHeight,
      isDiscountInvalidForProductId,
      maxDescriptionLength,
      onDiscountChange,
      richiediDatiVendita,
      setCartValue,
    ]
  );

  return (
    <>
      <ShopGridFilters
        searchFilter={searchFilter}
        onSearchFilterChange={setSearchFilter}
        divisionFilter={divisionFilter}
        onDivisionFilterChange={setDivisionFilter}
        divisionNames={divisionNames}
      />
      <div className="clearfix"></div>

      <ShopGridProvider value={ctxValue}>
        <Row xs={1} md={2} xl={3} xxl={4}>
          {filteredElements.map((element) => (
            <Col key={element.id} className="px-4 py-2 d-flex">
              <ShopProductCard element={element} divisionClassName={getDivisionClassName(classes, element.division)} />
            </Col>
          ))}
        </Row>

        <ShopCartDropdown
          cart={cart}
          displayName={displayName}
          color={color}
          elementsMap={elementsMap}
          hasColli={hasColli}
          maxDescriptionLength={maxDescriptionLength}
          richiediDatiVendita={Boolean(richiediDatiVendita)}
          discountValidationMessage={discountValidationMessage}
          discountsLoadError={discountsLoadError}
          onSendRequest={onSendRequest}
          sendDisabled={richiestaLoading || dialogOpen || Object.keys(cart).length === 0}
          sendLabel={richiestaLoading || dialogOpen ? "Caricamento…" : "Invia richiesta"}
          getScontoFinalValue={getScontoFinalValue}
          getEffectiveDiscountRaw={getEffectiveDiscountRaw}
          setCartValue={setCartValue}
        />

        <ShopFinalizeDialog
          open={dialogOpen}
          onClose={onDialogClose}
          onCloseAndReset={onDialogCloseAndReset}
          result={result}
          richiestaLoading={richiestaLoading}
          richiediDatiVendita={Boolean(richiediDatiVendita)}
          richiediExtra={Boolean(richiediExtra)}
          clientiOptions={clientiOptions}
          metodoPagamentiOptions={metodoPagamentoMapping}
          initialIdCliente={richiestaIdCliente}
          initialMetodoPagamento={richiestaMetodoPagamento}
          initialNote={richiestaNote}
          onSubmit={onDialogSubmit}
          cart={cart}
          elementsMap={elementsMap}
          getScontoFinalValue={getScontoFinalValue}
          redirectTo={redirectTo}
          discountValidationMessage={discountValidationMessage}
          serverErrorMessage={serverErrorMessage}
          richiestaExtra={richiestaExtra}
          onRichiestaExtraChange={setRichiestaExtra}
        />
      </ShopGridProvider>
    </>
  );
};

ShopGrid.propTypes = {
  elements: PropTypes.array.isRequired,
  cartName: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  richiediExtra: PropTypes.bool,
  richiediDatiVendita: PropTypes.bool,
  api: PropTypes.shape({
    add: PropTypes.func.isRequired,
  }).isRequired,
  redirectTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]).isRequired,
  maxDescriptionLength: PropTypes.number,
  imageHeight: PropTypes.number,
};

export { ShopGridSkeleton } from "./components/ShopGridSkeleton";

export {
  normalizeProductKey,
  getDiscountFinalValue,
  isValidDiscount,
  getInvalidDiscountProducts,
  buildMissingDiscountMessage,
  getDiscountsByDivision,
  validateDiscountsValuesJson,
} from "./utils/shopGridDiscounts";
